import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { DosageUom, DoseEntity, DoseType } from 'fitmed-models';
import { hasValue } from 'utils';
import * as PrescriptionDetailActions from './prescription-detail.actions';
import { PrescriptionApiService } from '@app/areas/prescriptions/services/prescription-api.service';
import {
  PrescriptionDetailState,
} from '@app/areas/prescriptions/components/prescription-detail/state/prescription-detail.reducer';
import { Store } from '@ngrx/store';
import {
  selectAllDoses,
} from '@app/areas/prescriptions/components/prescription-detail/state/prescription-detail.selectors';
import { AlertService } from '@services/helpers/alert/alert.service';

const plsTryAgain = 'Please try again.'

const messages = {
  prescriptionUpdateSuccess: 'Prescription updated successfully.',
  prescriptionUpdateFailure: `Prescription update failed. ${plsTryAgain}`,
}

@Injectable()
export class PrescriptionDetailEffects {
  updateDose$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrescriptionDetailActions.updateDose),
      withLatestFrom(this.store.select(selectAllDoses)),
      mergeMap(([action]) =>
        this.updateDose(
          action.update.id,
          action.update.prescriptionId,
          action.update.type,
          action.update.value,
          action.update.uom,
          action.assigning
        ).pipe(
          map(dose => {
            this.alertService.success('Dose updated successfully.');
            return PrescriptionDetailActions.updateDoseSuccess({ dose });
          }),
          catchError(error => {
            this.alertService.error('Failed to update dose. Please try again.');
            return of(
              PrescriptionDetailActions.updateDoseFailure({
                error,
                update: action.update,
              })
            );
          })
        )
      )
    )
  );

  loadDoses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrescriptionDetailActions.loadDoses),
      withLatestFrom(this.store.select(selectAllDoses)),
      mergeMap(([action, state]) => {
        const prescriptionsToLoad = action.prescriptions.filter(
          (id) => !state.checked.includes(id)
        );

        if (prescriptionsToLoad.length === 0) {
          return of(
            PrescriptionDetailActions.loadDosesSuccess({
              doses: [],
              prescriptions: prescriptionsToLoad,
            })
          );
        }

        return this.getDoses(prescriptionsToLoad, action.assigning).pipe(
          map((doses) => {
            return PrescriptionDetailActions.loadDosesSuccess({
              doses,
              prescriptions: prescriptionsToLoad,
            });
          }),
          catchError((error) => {
            this.alertService.error('Failed to load doses. Please try again.');
            return of(
              PrescriptionDetailActions.loadDosesFailure({
                error,
                prescriptions: prescriptionsToLoad,
              })
            );
          })
        );
      })
    )
  );

  constructor(
    private store: Store<PrescriptionDetailState>,
    private actions$: Actions,
    private prescriptionApiService: PrescriptionApiService,
    private alertService: AlertService
  ) {}

  private updateDose(
    id: string | null,
    prescriptionId: string,
    type: DoseType,
    value: string,
    uom: DosageUom,
    assigning: boolean,
  ): Observable<DoseEntity> {
    console.log('assigning', assigning)
    const p = {
      id,
      prescriptionId,
      type,
      value,
      uom,
    }

    if (assigning) {
      return this.prescriptionApiService.upsertAssignedDose(p)
    }

    return this.prescriptionApiService.upsertAuthoredDose(p);
  }

  private getDoses(prescriptionIds: string[], assigning): Observable<DoseEntity[]> {
    if (!hasValue(prescriptionIds)) {
      return of([]);
    }

    if (assigning) {
      return this.prescriptionApiService.getAssignedDoses(prescriptionIds);
    }

    return this.prescriptionApiService.getDoses(prescriptionIds);
  }
}
